<template>
  <div class="technicians-page">
    <div class="page-title-bar">
      <h2 class="page-title">Technicians</h2>
    </div>

    <div class="page-content">
      <div class="search-bar">
        <div class="search-wrapper" :class="{ 'is-open': showDropdown }">
          <div
            class="search-select-wrapper"
            :class="{ 'is-open': showDropdown }"
            @click="toggleDropdown"
          >
            <span class="search-select-label">{{ currentSearchType }}</span>
            <img :src="downarrowIcon" alt="arrow" class="dropdown-arrow" />
          </div>
          <input
            v-model="searchKeyword"
            type="text"
            class="search-input"
            maxlength="50"
            placeholder="Search.."
            @keyup.enter="handleSearch"
          />
          <div v-if="showDropdown" class="dropdown-menu">
            <div
              v-for="option in searchTypeOptions"
              :key="option.value"
              class="dropdown-item"
              :class="{ selected: searchType === option.value }"
              @click="selectSearchType(option.value)"
            >
              {{ option.label }}
            </div>
          </div>
        </div>
        <div class="search-buttons">
          <button class="btn btn-search" @click="handleSearch">Search</button>
          <button class="btn btn-clear" @click="handleClear">Clear</button>
        </div>
      </div>

      <div class="action-bar">
        <span
          class="count-info"
          :class="{ 'count-info--pending': !showInitialPlaceholder && pageView === 'loading' }"
        >
          {{ showInitialPlaceholder ? 0 : total }} Technicians
        </span>
        <button class="btn btn-new" @click="handleNewTechnician">+ NEW</button>
      </div>

      <div class="list-body" :aria-busy="!showInitialPlaceholder && pageView === 'loading'">
        <LoadingIndicator v-if="!showInitialPlaceholder && pageView === 'loading'" />
        <div
          v-if="showInitialPlaceholder || (pageView === 'content' && total === 0)"
          class="orders-empty"
        >
          <img :src="imgListBlank" class="orders-empty-img" alt="" />
          <p class="orders-empty-msg">No technicians found</p>
        </div>
        <div v-else-if="pageView === 'content' && total > 0" class="technicians-list">
        <div v-for="tech in techniciansList" :key="tech.id" class="technician-card">
          <div class="card-header">
            <span class="tech-id" :title="tech.technicianNo">
              <span class="tech-label">Technician ID:</span>
              {{ formattedTechnicianNo(tech.technicianNo) }}
            </span>
            <span class="tech-mobile">
              <span class="tech-label">Registered Mobile:</span>
              {{ tech.mobile }}
            </span>
          </div>
          <div class="card-body">
            <div class="column-user">
              <div class="tech-avatar-wrapper">
                <img
                  v-if="tech.avatar"
                  :src="getAvatarUrl(tech.avatar)"
                  alt="avatar"
                  class="tech-avatar"
                  @error="handleAvatarError"
                />
                <img v-else :src="defaultAvatar" alt="avatar" class="tech-avatar" />
              </div>
              <div class="user-details">
                <div class="user-name">{{ tech.name }}</div>
                <div class="user-upcoming">
                  Upcoming:
                  <span
                    class="upcoming-text"
                    :class="{ 'upcoming-none': !isUpcomingClickable(tech) }"
                    @click="handleUpcomingClick(tech)"
                  >
                    {{ tech.upcomingPlanLabel }}
                  </span>
                </div>
                <div v-if="tech.upcomingDisplay" class="user-date">
                  {{ tech.upcomingDisplay }}
                </div>
              </div>
            </div>

            <div class="column-role">
              <div class="role-content">
                <div class="role-info" :title="tech.workRoleText">
                  {{ tech.workRoleText }}
                </div>
                <div
                  v-if="tech.licenseNo"
                  class="licence-info"
                  :data-full-text="tech.licenseNo.length > 10 ? tech.licenseNo : ''"
                >
                  <img :src="checkIcon" alt="certified" class="cert-icon" />
                  {{
                    tech.licenseNo.length > 10 ? tech.licenseNo.slice(0, 10) + '..' : tech.licenseNo
                  }}
                </div>
              </div>
            </div>

            <div class="column-orders">
              <div
                v-for="(line, index) in tech.orderDisplayLines"
                :key="`${tech.id}-order-${index}`"
                class="order-item"
                :class="{ 'order-item--clickable': isOrderLineClickable(line) }"
                @click="handleOrderLineClick(tech, line)"
              >
                <template v-if="line.display">
                  <span class="order-text">{{ line.display }}</span>
                </template>
                <template v-else-if="line.label && line.count != null">
                  <span class="order-text">{{ line.label }}</span>
                  <span class="order-count">({{ line.count }})</span>
                </template>
              </div>
              <div
                v-if="tech.orderDisplayLines.length === 0 && tech.noOrdersText"
                class="no-orders"
              >
                {{ tech.noOrdersText }}
              </div>
            </div>

            <div class="column-status">
              <div class="status-content">
                <div
                  class="status-badge"
                  :class="{ inactive: tech.status !== TECHNICIAN_LIST_STATUS_ACTIVE }"
                >
                  {{ tech.statusText }}
                </div>
                <a
                  href="javascript:void(0)"
                  class="view-details"
                  @click="handleViewDetails(tech.id)"
                >
                  View Details
                </a>
              </div>
            </div>

            <div class="column-actions">
              <div class="actions-box">
                <button class="action-btn" @click="handleUpdateMobile(tech.id)">
                  <img :src="mobileIcon" alt="mobile" class="action-icon-img" />
                  Update Mobile
                </button>
                <button class="action-btn" @click="handleEditTechnician(tech.id)">
                  <img :src="editIcon" alt="edit" class="action-icon-img" />
                  Edit
                </button>
                <button
                  v-if="tech.status === TECHNICIAN_LIST_STATUS_ACTIVE"
                  class="action-btn"
                  @click="handleInactivate(tech.id)"
                >
                  <img :src="scheduleIcon" alt="schedule" class="action-icon-img" />
                  Inactivate
                </button>
                <button v-else class="action-btn" @click="handleReactivate(tech.id)">
                  <img :src="activateIcon" alt="activate" class="action-icon-img" />
                  Reactivate
                </button>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      <div class="pagination-wrapper">
        <PageComponent
          :current-page="currentPage"
          :total-pages="Math.max(1, totalPages)"
          @page-change="handlePageChange"
        />
      </div>
    </div>

    <!-- 所有弹窗组件 -->
    <NewTechnicians v-if="showNewModal" @close="showNewModal = false" @saved="handleNewSaved" />

    <DetailTechnicians
      v-if="showDetailModal"
      :technician-id="currentTechId"
      @close="showDetailModal = false"
      @updated="handleDetailUpdated"
    />

    <EditTechnicians
      v-if="showEditModal"
      :technician-id="currentTechId"
      @close="showEditModal = false"
      @saved="handleEditSaved"
    />

    <MobileStatus
      v-if="showMobileModal"
      :technician-id="currentTechId"
      mode="update"
      @close="showMobileModal = false"
      @saved="handleMobileSaved"
    />

    <MobileStatus
      v-if="showDeactivateModal"
      :technician-id="currentTechId"
      :mode="deactivateMode"
      @close="showDeactivateModal = false"
      @saved="handleDeactivateSaved"
    />

    <div class="toast-area">
      <ToastComponent :visible="toast.visible" :type="toast.type" :message="toast.message" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted, provide } from 'vue'
import { useRouter } from 'vue-router'
import { useTechniciansStore, type Technician } from '@/api/technicians'
import {
  buildTechnicianSearchTypes,
  DEFAULT_TECHNICIAN_SEARCH_TYPES,
  getTechnicianSearchTypeDisplayLabel,
  postTechnicianMeta,
  searchTechnicianList,
  TECHNICIAN_LIST_STATUS_ACTIVE,
  type OrderStats,
  type Pagination,
  type TechnicianListItem,
  type TechnicianListParams,
  type TechnicianMetaData,
  type TechnicianSearchTypeOption,
} from '@/api/technicianslist.ts'
import {
  buildOrderStatLabelMap,
  normalizeMobileSearchKeyword,
  resolveTechnicianOrderLines,
  resolveUpcomingDisplay,
  resolveUpcomingPlanLabel,
  resolveUpcomingService,
  resolveWorkRoleText,
  type OrderStatLabelMap,
  type TechnicianOrderDisplayLine,
} from '@/utils/technicianListDisplay'
import { writeHmCareOrdersListPrefill } from '@/api/CareOrderDetailFocus'
import { careOrdersDetailRoute, careOrdersListRouteForSub } from '@/utils/careOrdersRoutes'
import { getTechnicianDetail, type TechnicianDetail } from '@/api/detailTechnicians'
import mobileIcon from '@/assets/mobile.png'
import editIcon from '@/assets/edit.png'
import scheduleIcon from '@/assets/Schedule.png'
import activateIcon from '@/assets/activate.png'
import downarrowIcon from '@/assets/downarrow.png'

import checkIcon from '@/assets/normal@2x.png'
import defaultAvatar from '@/assets/homemetric_green@2x.png'
import imgListBlank from '@/assets/list_blank@2x.png'
import ToastComponent from '@/components/ToastComponent.vue'
import { TOAST_DURATION } from '@/constants/toast'
import PageComponent from '@/components/PageComponent.vue'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import NewTechnicians from './NewTechnicians.vue'
import DetailTechnicians from './DetailTechnicians.vue'
import EditTechnicians from './EditTechnicians.vue'
import MobileStatus from './MobileStatus.vue'

type PageView = 'initial-empty' | 'loading' | 'content'

const techniciansStore = useTechniciansStore()
const router = useRouter()
const searchKeyword = ref('')
const searchType = ref(DEFAULT_TECHNICIAN_SEARCH_TYPES[0]?.value ?? '')
const searchTypeOptions = ref<TechnicianSearchTypeOption[]>([...DEFAULT_TECHNICIAN_SEARCH_TYPES])
const showDropdown = ref(false)
const total = ref(0)
const totalPagesFromApi = ref(1)
const currentPage = ref(1)
const pageSize = 10
const avatarBaseUrl = import.meta.env.VITE_BASE_URL || ''
const pageView = ref<PageView>('initial-empty')
const showInitialPlaceholder = ref(true)
const techniciansList = ref<Technician[]>([])
const orderStatLabelMap = ref<OrderStatLabelMap>({})
const INITIAL_EMPTY_MIN_MS = 500
let initialLoadTimer: ReturnType<typeof setTimeout> | null = null
let fetchGeneration = 0

const formattedTechnicianNo = computed(() => (techNo: string) => {
  if (techNo.length > 8) {
    return techNo.slice(0, 8) + '..'
  }
  return techNo
})

// 弹窗状态管理
const showNewModal = ref(false)
const showDetailModal = ref(false)
const showEditModal = ref(false)
const showMobileModal = ref(false)
const showDeactivateModal = ref(false)
const currentTechId = ref('')
const deactivateMode = ref<'deactivate' | 'reactivate'>('deactivate')

const toast = reactive({
  visible: false,
  type: 'success' as 'success' | 'warning' | 'error',
  message: '',
})

function getAvatarUrl(avatar: string): string {
  if (!avatar || avatar.trim() === '') return ''
  if (avatar.startsWith('http')) return avatar
  return `${avatarBaseUrl}${avatar}`
}
function handleAvatarError(event: Event): void {
  const img = event.target as HTMLImageElement
  img.src = defaultAvatar
}

// formatDate 函数已移除，因为不再需要显示创建时间

function mapOrderStats(raw: TechnicianListItem['order_stats']): OrderStats {
  if (!raw || Array.isArray(raw)) {
    return { hmc_orders: 0, fu_orders: 0, ad_orders: 0 }
  }
  return {
    hmc_orders: raw.hmc_orders ?? 0,
    fu_orders: raw.fu_orders ?? 0,
    ad_orders: raw.ad_orders ?? 0,
  }
}

function mapListItemFields(item: TechnicianListItem) {
  const upcomingService = resolveUpcomingService(item.upcoming_service)
  return {
    workRoleText: resolveWorkRoleText(item),
    orderDisplayLines: resolveTechnicianOrderLines(item, orderStatLabelMap.value),
    upcomingService,
    upcomingPlanLabel: resolveUpcomingPlanLabel(item),
    upcomingDisplay: resolveUpcomingDisplay(item),
  }
}

// 已删除未使用的 getUpcomingServiceData、getUpcomingDateTime 和 formatTime 函数

function showToast(type: 'success' | 'warning' | 'error', message: string): void {
  toast.type = type
  toast.message = message
  toast.visible = true
  setTimeout(() => {
    toast.visible = false
  }, TOAST_DURATION.brief)
}

function isInspectorRole(workRole: string): boolean {
  return workRole.trim().toLowerCase().includes('inspector')
}

function resolveUpcomingOrderId(tech: Technician): string {
  const svc = tech.upcomingService
  if (!svc || typeof svc === 'string') return ''
  return String(svc.order_id ?? '').trim()
}

function isUpcomingClickable(tech: Technician): boolean {
  return Boolean(resolveUpcomingOrderId(tech))
}

function handleUpcomingClick(tech: Technician): void {
  if (!isUpcomingClickable(tech)) return
  const orderId = resolveUpcomingOrderId(tech)
  if (!orderId) {
    showToast('warning', 'Order not found.')
    return
  }
  writeHmCareOrdersListPrefill({
    searchField: 'order_id',
    searchQuery: orderId,
    careSubKey: 'hm_orders',
  })
  void router.push(careOrdersDetailRoute(orderId))
}

function isAdOrderStatKey(statKey: string | undefined): boolean {
  const key = (statKey ?? '').trim().toLowerCase()
  return key === 'ad_orders' || key === 'ad'
}

function isOrderLineClickable(line: TechnicianOrderDisplayLine): boolean {
  if (isAdOrderStatKey(line.statKey)) return false
  if (line.count != null && line.count <= 0) return false
  return Boolean(line.label.trim() || line.display.trim())
}

function handleOrderLineClick(tech: Technician, line: TechnicianOrderDisplayLine): void {
  if (!isOrderLineClickable(line)) return

  const id = tech.id
  const name = tech.name
  if (isInspectorRole(tech.workRole)) {
    writeHmCareOrdersListPrefill({ inspectorId: id, inspectorName: name })
  } else {
    writeHmCareOrdersListPrefill({ technicianId: id, technicianName: name })
  }
  void router.push(careOrdersListRouteForSub('hm_orders'))
}

provide<(type: 'success' | 'warning' | 'error', message: string) => void>('showToast', showToast)

// 弹窗事件处理
function handleViewDetails(id: string | number): void {
  currentTechId.value = String(id)
  showDetailModal.value = true
}

function handleEditTechnician(id: string | number): void {
  currentTechId.value = String(id)
  showEditModal.value = true
}

function handleUpdateMobile(id: string | number): void {
  currentTechId.value = String(id)
  showMobileModal.value = true
}

function handleInactivate(id: string | number): void {
  currentTechId.value = String(id)
  deactivateMode.value = 'deactivate'
  showDeactivateModal.value = true
}

function handleReactivate(id: string | number): void {
  currentTechId.value = String(id)
  deactivateMode.value = 'reactivate'
  showDeactivateModal.value = true
}

function handleDetailUpdated(technicianId: string): void {
  void updateSingleTechnician(technicianId)
}

async function updateSingleTechnician(technicianId: string): Promise<void> {
  try {
    const response = await getTechnicianDetail(technicianId)
    if (response.code !== 200 || !response.data) return

    const updated = mapDetailToTechnician(response.data)
    const index = techniciansStore.technicians.findIndex((tech) => tech.id === technicianId)
    if (index !== -1) {
      techniciansStore.technicians[index] = updated
    }
    const listIndex = techniciansList.value.findIndex((tech) => tech.id === technicianId)
    if (listIndex !== -1) {
      techniciansList.value[listIndex] = updated
    }
  } catch (err) {
    console.error('Failed to update single technician:', err)
    void refreshTechnicians()
  }
}

function mapDetailToTechnician(item: TechnicianDetail): Technician {
  const listLikeItem: TechnicianListItem = {
    id: item.id,
    technician_no: item.technician_no,
    name: item.name,
    avatar: item.avatar,
    phone_code: item.phone_code,
    phone: item.phone,
    mobile: item.mobile,
    work_role: item.work_role,
    order_stats: item.order_stats,
  }
  const mappedFields = mapListItemFields(listLikeItem)
  return {
    id: String(item.id),
    technicianNo: item.technician_no,
    name: item.name,
    mobile: item.mobile || item.phone,
    avatar: item.avatar || '',
    workRole: item.work_role,
    workRoleText: mappedFields.workRoleText,
    licenseNo: item.license_no || '',
    sinType: item.sin_type || '',
    sinNumber: item.sin_number || '',
    residencyStatus: item.residency_status || '',
    address: item.address || '',
    email: item.email || '',
    settlementConfig: (item.settlement_config || {}) as Record<string, unknown>,
    status: item.status ?? 0,
    statusText: item.status_text ?? '',
    orderStats: mapOrderStats(item.order_stats),
    orderDisplayLines: mappedFields.orderDisplayLines,
    noOrdersText: '',
    upcomingService: mappedFields.upcomingService,
    upcomingPlanLabel: mappedFields.upcomingPlanLabel,
    upcomingDisplay: mappedFields.upcomingDisplay,
    createTime: item.create_time || '',
    updateTime: item.update_time || '',
  }
}

// 保存成功后的处理
function handleNewSaved(): void {
  showNewModal.value = false
  void refreshTechnicians()
}
function handleEditSaved(): void {
  showEditModal.value = false
  void refreshTechnicians()
  showToast('success', 'Successfully edited')
}
function handleMobileSaved(): void {
  showMobileModal.value = false
  void refreshTechnicians()
}

function handleDeactivateSaved(): void {
  showDeactivateModal.value = false
  void refreshTechnicians()
}

const currentSearchType = computed(() => {
  const hit = searchTypeOptions.value.find((option) => option.value === searchType.value)
  if (hit) return getTechnicianSearchTypeDisplayLabel(hit)
  const fallback = searchTypeOptions.value[0]
  return fallback ? getTechnicianSearchTypeDisplayLabel(fallback) : 'Mobile No.'
})

function defaultSearchTypeValue(): string {
  return searchTypeOptions.value[0]?.value ?? DEFAULT_TECHNICIAN_SEARCH_TYPES[0]?.value ?? ''
}

function ensureSearchTypeValid(): void {
  const values = new Set(searchTypeOptions.value.map((option) => option.value))
  if (!values.has(searchType.value)) {
    searchType.value = defaultSearchTypeValue()
  }
}

function toggleDropdown(): void {
  showDropdown.value = !showDropdown.value
}

function selectSearchType(type: string): void {
  if (searchType.value === type) {
    showDropdown.value = false
    return
  }
  searchType.value = type
  showDropdown.value = false
  if (searchKeyword.value.trim()) {
    currentPage.value = 1
    void refreshTechnicians()
  }
}

function handleClickOutside(event: MouseEvent): void {
  const target = event.target as HTMLElement
  if (!target.closest('.search-select-wrapper')) {
    showDropdown.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  scheduleInitialLoad()
})

onUnmounted(() => {
  if (initialLoadTimer) {
    clearTimeout(initialLoadTimer)
    initialLoadTimer = null
  }
  document.removeEventListener('click', handleClickOutside)
})

const totalPages = computed(() => Math.max(1, totalPagesFromApi.value))

function applyTechnicianList(rawList: TechnicianListItem[]): void {
  const mapped = rawList.map(mapListItemToTechnician)
  techniciansStore.technicians = mapped
  techniciansList.value = mapped
}

function applyListPagination(pagination: Pagination | undefined): void {
  total.value = pagination?.total ?? 0
  totalPagesFromApi.value = Math.max(1, pagination?.total_pages ?? 1)
}

function resetListState(): void {
  techniciansList.value = []
  techniciansStore.technicians = []
  total.value = 0
  totalPagesFromApi.value = 1
}

function mapListItemToTechnician(item: TechnicianListItem): Technician {
  const mappedFields = mapListItemFields(item)
  return {
    id: String(item.id),
    technicianNo: item.technician_no,
    name: item.name,
    mobile: item.mobile || item.phone,
    avatar: item.avatar || '',
    workRole: item.work_role,
    workRoleText: mappedFields.workRoleText,
    licenseNo: item.license_no || '',
    sinType: item.sin_type || '',
    sinNumber: item.sin_number || '',
    residencyStatus: item.residency_status || '',
    address: item.address || '',
    email: item.email || '',
    settlementConfig: item.settlement_config || {},
    status: item.status ?? 0,
    statusText: item.status_text ?? '',
    orderStats: mapOrderStats(item.order_stats),
    orderDisplayLines: mappedFields.orderDisplayLines,
    noOrdersText: item.no_orders_text ?? '',
    upcomingService: mappedFields.upcomingService,
    upcomingPlanLabel: mappedFields.upcomingPlanLabel,
    upcomingDisplay: mappedFields.upcomingDisplay,
    createTime: item.create_time || '',
    updateTime: item.update_time || '',
  }
}

function handleSearch(): void {
  if (searchKeyword.value.length > 50) {
    showToast('error', 'Search keyword cannot exceed 50 characters')
    return
  }
  currentPage.value = 1
  void refreshTechnicians()
}

function handleClear(): void {
  searchKeyword.value = ''
  searchType.value = defaultSearchTypeValue()
  currentPage.value = 1
  void refreshTechnicians()
}

function handlePageChange(page: number): void {
  currentPage.value = page
  void refreshTechnicians()
}

function handleNewTechnician(): void {
  showNewModal.value = true
}

function scheduleInitialLoad(): void {
  showInitialPlaceholder.value = true
  pageView.value = 'initial-empty'
  techniciansList.value = []

  if (initialLoadTimer) {
    clearTimeout(initialLoadTimer)
  }
  initialLoadTimer = setTimeout(() => {
    initialLoadTimer = null
    void beginDataLoad()
  }, INITIAL_EMPTY_MIN_MS)
}

async function loadTechnicianMeta(): Promise<void> {
  try {
    const response = await postTechnicianMeta({ page: 1, page_size: 10 })
    if (response.code === 200) {
      const meta = response.data as TechnicianMetaData
      orderStatLabelMap.value = buildOrderStatLabelMap(meta)
      searchTypeOptions.value = buildTechnicianSearchTypes(meta)
      ensureSearchTypeValid()
    }
  } catch (err) {
    console.error('Failed to load technician meta:', err)
    searchTypeOptions.value = [...DEFAULT_TECHNICIAN_SEARCH_TYPES]
    ensureSearchTypeValid()
  }
}

async function beginDataLoad(): Promise<void> {
  showInitialPlaceholder.value = false
  pageView.value = 'loading'
  try {
    await loadTechnicianMeta()
    await fetchTechniciansData()
  } finally {
    pageView.value = 'content'
  }
}

async function refreshTechnicians(): Promise<void> {
  showInitialPlaceholder.value = false
  pageView.value = 'loading'
  techniciansList.value = []
  try {
    await fetchTechniciansData()
  } finally {
    pageView.value = 'content'
  }
}

function isMobileSearchType(type: string): boolean {
  const normalized = type.trim().toLowerCase()
  return normalized === 'mobile_no' || normalized === 'mobile' || normalized.includes('mobile')
}

function buildSearchKeyword(type: string, keyword: string): string {
  if (!isMobileSearchType(type)) return keyword
  return normalizeMobileSearchKeyword(keyword)
}

async function fetchTechniciansData(): Promise<void> {
  const generation = ++fetchGeneration
  try {
    const keyword = searchKeyword.value.trim()
    const searchParams: Omit<TechnicianListParams, 'page' | 'page_size'> = {}
    if (keyword) {
      searchParams.search_type = searchType.value
      searchParams.keyword = buildSearchKeyword(searchType.value, keyword)
    }

    const params: TechnicianListParams = {
      page: currentPage.value,
      page_size: pageSize,
      ...searchParams,
    }

    const response = await searchTechnicianList(params)
    if (generation !== fetchGeneration) return

    if (response.code === 200) {
      applyTechnicianList(response.data?.list || [])
      applyListPagination(response.data?.pagination)
    } else {
      resetListState()
      showToast('error', response.msg || 'Failed to fetch')
    }
  } catch (err) {
    if (generation !== fetchGeneration) return
    console.error('Network error:', err)
    resetListState()
    showToast('error', 'Network request failed')
  }
}
</script>
<style scoped>
.technicians-page {
  width: 100%;
  padding: 0;
  height: auto;
  overflow: visible;
}
.page-title {
  font-size: 18px;
  font-weight: 600;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: -10px 0 16px -10px;
}

.page-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.search-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #fff;
  border-radius: 16px;
  margin-left: -9px;
  margin-right: -12px;
  border: 1px solid #e8e8e8;
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0;
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 24px;
  background: #fff;
  width: 273px;
  height: 32px;
  flex-shrink: 0;
  box-sizing: border-box;
  transition: border-color 0.15s ease;
}

.search-wrapper:focus-within,
.search-wrapper.is-open {
  border-color: #666666;
}

.search-select-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-right: 12px;
  cursor: pointer;
  user-select: none;
  position: relative;
}

.search-select-wrapper::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 16px;
  background-color: #e8e8e8;
}

.search-select-label {
  font-size: 14px;
  color: #000000;
  font-weight: 600;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  white-space: nowrap;
}
.dropdown-arrow {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  object-fit: contain;
  pointer-events: none;
  transition: transform 0.2s ease;
  filter: brightness(0) saturate(100%) invert(42%) sepia(0%) saturate(0%) hue-rotate(0deg)
    brightness(100%) contrast(100%);
}

.search-select-wrapper.is-open .dropdown-arrow {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: -1px;
  width: 273px;
  box-sizing: border-box;
  padding: 4px 0;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dropdown-item {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  padding: 8px 16px;
  font-size: 14px;
  line-height: 16px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s;
}

.dropdown-item:hover {
  background: #f5f5f5;
}

.dropdown-item.selected {
  font-weight: 600;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  font-weight: 400;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  background: transparent;
  color: #000000;
  padding-left: 12px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
}

.search-input::placeholder {
  color: #cccccc;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
}

.search-buttons {
  display: flex;
  gap: 16px;
  flex-shrink: 0;
}

.btn {
  border-radius: 16px;
  font-size: 14px;
  font-weight: 700;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  height: 32px;
  width: 80px;
}

.btn-search {
  background: #000;
  color: #fff;
}

.btn-search:hover {
  background: #484848;
}

.btn-clear {
  background: #b0b0b0;
  color: #fff;
}

.btn-clear:hover {
  background: #CCCCCC;
}

.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.count-info {
  font-size: 16px;
  font-weight: 600;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  min-height: 24px;
}

.count-info--pending {
  visibility: hidden;
}

.list-body {
  position: relative;
}

.btn-new {
  background: #00aeef;
  color: #fff;
  font-weight: 700;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  border-radius: 16px;
  height: 32px;
  width: 80px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: -10px;
}

.btn-new:hover {
  background: #2a5d8f;
}

.technicians-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: auto;
  overflow: visible;
}

.technician-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  margin-left: -8px;
  margin-right: -10px;
  position: relative;
  z-index: 1;
}

.technician-card:has(.licence-info[data-full-text]:not([data-full-text='']):hover) {
  z-index: 200;
  overflow: visible;
}

.card-header {
  width: 100%;
  padding: 6px 24px 6px 16px;
  background: #f7f7f7;
  border-bottom: 1px solid #e0e0e0;
  border-top-left-radius: 16px;
  border-top-right-radius: 15px;
  display: flex;
  gap: 17px;
  font-size: 0.8rem;
  align-items: center;
  flex-wrap: wrap;
}

.tech-label {
  color: #999;
}

.tech-id,
.tech-mobile {
  font-size: 14px;
  font-weight: 400;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.tech-id {
  display: inline-block;
  width: 170px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-body {
  display: grid;
  grid-template-columns: 528fr 160fr 160fr 160fr 160fr;
  min-height: 109px;
  height: auto;
  overflow: visible;
}
.card-body > div {
  padding: 16px;
  border-right: 1px solid #e0e0e0;
  display: flex;
  align-items: stretch;
  justify-content: center;
}
/* ========== 中间列居中（Role / Orders / Status） ========== */
.column-role,
.column-orders,
.column-status {
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

/* ========== 按钮列靠左 ========== */
.column-actions {
  align-items: flex-start;
  justify-content: flex-start;
}
.card-body > div:last-child {
  border-right: none;
}

/* ========== 第一列用户信息左对齐（和Checklist的column-main一致） ========== */
.column-user {
  justify-content: flex-start !important;
  align-items: flex-start !important;
  gap: 12px;
}

.tech-avatar-wrapper {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}
.tech-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.user-details {
  gap: 8px;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
}

.user-name {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  word-break: break-word;
}

.user-upcoming {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.upcoming-text {
  color: #000000;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-decoration: underline;
  cursor: pointer;
}

.upcoming-none {
  color: #000000;
  text-decoration: underline;
  cursor: default;
}

.user-date {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.column-role {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
  min-width: 160px;
  width: auto;
}
.role-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  overflow: visible;
  position: relative;
}
.role-info {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  text-align: center;
  min-width: 160px;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.licence-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  white-space: nowrap;
  flex-shrink: 0;
  position: relative;
}
.licence-info[data-full-text]:not([data-full-text='']) {
  cursor: pointer;
}
.licence-info:not([data-full-text]),
.licence-info[data-full-text=''] {
  cursor: default;
  pointer-events: none;
}
.licence-info:hover::after {
  content: '';
}
.licence-info[data-full-text]:not([data-full-text='']):hover::after {
  content: attr(data-full-text);
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: #fef39c;
  color: #000;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  white-space: pre-wrap;
  word-break: break-all;
  z-index: 201;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  width: auto;
  min-width: 200px;
  max-width: 400px;
  max-height: 200px;
  overflow-y: auto;
  line-height: 1.5;
  pointer-events: none;
}

.cert-icon {
  width: 12px;
  height: 12px;
  object-fit: contain;
  flex-shrink: 0;
  }
.column-orders {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  justify-content: center;
  min-width: 160px;
  width: auto;
text-align: center;
}

.order-item {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0;
  padding: 0;
  text-align: center;
  word-wrap: break-word;
  white-space: normal;
  width: 100%;
}

.order-item--clickable {
  cursor: pointer;
}
.no-orders {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  margin: 0;
  padding: 0;
  color: #000000;
  text-align: center;
  width: auto;
}
.order-text {
  color: #000000;
  font-weight: 400;
  line-height: 20px;
  text-decoration: underline;
}

.order-count {
  color: #000000;
  font-weight: 400;
  line-height: 20px;
  margin-left: 4px;
  text-decoration: underline;
}

.column-status {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  justify-content: center;
  min-width: 160px;
  width: auto;
  text-align: center;
}
.status-content {
  min-width: 80px;
  width: 100%;
  height: 44px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
  align-items: center;
  margin: 0;
  padding: 0;
}
.status-badge {
  font-size: 14px;
  font-weight: 600;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #00aeef;
  text-align: center;
  white-space: nowrap;
}

.status-badge.inactive {
  color: #ff4d4f;
}

.view-details {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  text-decoration: none;
  background-color: transparent;
  text-align: center;
  white-space: nowrap;
}

.view-details:hover {
  text-decoration: none;
}


.column-actions {
  flex-direction: column;
  gap: 8px;
  max-width: 160px;
}

.actions-box {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  padding: 0;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  transition: all 0.2s;
  border-radius: 8px;
  white-space: nowrap;
  flex-shrink: 0;
}

.action-icon-img {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.orders-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 16px 32px;
  box-sizing: border-box;
}

.orders-empty-img {
  width: 200px;
  height: 200px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

.orders-empty-msg {
  margin: 12px 0 0;
  font-size: 24px;
  font-weight: 500;
  color: #b0b0b0;
}

/* stylelint-disable-next-line no-unused-selectors */
.loading-state {
  padding: 40px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

/* stylelint-disable-next-line no-unused-selectors */
.loading-img {
  width: 200px;
  height: 200px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

/* stylelint-disable-next-line no-unused-selectors */
.loading-text {
  font-size: 14px;
  color: #999;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.pagination-wrapper {
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
  margin-right: -10px;
}

.toast-area {
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 99999;
}

::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}
::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}
::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

@media (max-width: 1200px) {
  .card-body {
    grid-template-columns: 1fr 1fr;
  }
  .card-body > div {
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
  }
}

@media (max-width: 768px) {
  .card-body {
    grid-template-columns: 1fr;
  }
  .search-bar {
    flex-direction: column;
  }
  .search-wrapper {
    width: 100%;
  }
}
</style>
